import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Lock, User, Loader2, Eye, EyeOff, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const AppleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

// Saved emails helper
const SAVED_EMAILS_KEY = 'bookeasy_saved_emails';
export function getSavedEmails() {
  try { return JSON.parse(localStorage.getItem(SAVED_EMAILS_KEY) || '[]'); } catch { return []; }
}
export function addSavedEmail(email) {
  if (!email) return;
  const emails = getSavedEmails().filter(e => e !== email);
  emails.unshift(email);
  localStorage.setItem(SAVED_EMAILS_KEY, JSON.stringify(emails.slice(0, 10)));
}
export function removeSavedEmail(email) {
  const emails = getSavedEmails().filter(e => e !== email);
  localStorage.setItem(SAVED_EMAILS_KEY, JSON.stringify(emails));
}

export function AuthModal({ isOpen, onClose, onSuccess }) {
  const { t } = useLanguage();
  const { login: googleLogin } = useAuth();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [savedEmails, setSavedEmails] = useState(getSavedEmails());

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        if (!form.name || form.name.length < 2) {
          toast.error("Inserisci un nome valido");
          setLoading(false);
          return;
        }
        try {
          const res = await axios.post(`${API}/auth/register`, form, { withCredentials: true });
          if (res.data?.session_token) localStorage.setItem('bookeasy_session', res.data.session_token);
          addSavedEmail(form.email);
          toast.success("Registrazione completata!");
          onSuccess?.();
          onClose();
          if (!res.data.role) {
            window.location.href = '/role-select';
          } else {
            window.location.href = '/dashboard';
          }
        } catch (regErr) {
          // If email already registered, auto-login instead
          if (regErr.response?.status === 400 && regErr.response?.data?.detail?.includes("registrata")) {
            try {
              const loginRes = await axios.post(`${API}/auth/login`, { email: form.email, password: form.password }, { withCredentials: true });
              if (loginRes.data?.session_token) localStorage.setItem('bookeasy_session', loginRes.data.session_token);
              addSavedEmail(form.email);
              toast.success("Email gia registrata — accesso effettuato!");
              onSuccess?.();
              onClose();
              if (!loginRes.data.role) {
                window.location.href = '/role-select';
              } else {
                window.location.href = '/dashboard';
              }
            } catch {
              toast.error("Email gia registrata. Prova ad accedere con la password corretta.");
            }
          } else {
            // Show specific error from backend
            const regDetail = regErr.response?.data?.detail;
            let regMsg = "Errore durante la registrazione";
            if (typeof regDetail === "string") {
              regMsg = regDetail;
            } else if (Array.isArray(regDetail) && regDetail.length > 0) {
              const field = regDetail[0]?.loc?.slice(-1)[0] || "";
              if (field === "password") regMsg = "La password deve avere almeno 6 caratteri";
              else if (field === "email") regMsg = "Inserisci un'email valida";
              else regMsg = regDetail[0]?.msg || regMsg;
            } else if (!regErr.response) {
              regMsg = "Errore di connessione. Controlla la tua rete.";
            }
            toast.error(regMsg);
          }
        }
      } else {
        const res = await axios.post(`${API}/auth/login`, { email: form.email, password: form.password }, { withCredentials: true });
        if (res.data?.session_token) localStorage.setItem('bookeasy_session', res.data.session_token);
        addSavedEmail(form.email);
        toast.success("Accesso effettuato!");
        onSuccess?.();
        onClose();
        if (!res.data.role) {
          window.location.href = '/role-select';
        } else {
          window.location.href = '/dashboard';
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMsg = "Errore durante l'autenticazione";
      if (typeof detail === "string") {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation errors
        const field = detail[0]?.loc?.slice(-1)[0] || "";
        const msg = detail[0]?.msg || "";
        if (field === "password" && msg.includes("6")) {
          errorMsg = "La password deve avere almeno 6 caratteri";
        } else if (field === "email") {
          errorMsg = "Inserisci un'email valida";
        } else if (field === "name") {
          errorMsg = "Inserisci un nome valido";
        } else {
          errorMsg = msg || errorMsg;
        }
      } else if (!err.response) {
        errorMsg = "Errore di connessione. Controlla la tua rete.";
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    googleLogin();
  };

  const handleAppleLogin = async () => {
    setAppleLoading(true);
    try {
      // Fetch Apple config from backend
      const configRes = await axios.get(`${API}/auth/apple/config`);
      const { configured, client_id } = configRes.data;

      if (!configured || !client_id) {
        toast.error(
          "Apple Sign In non ancora configurato. Contatta l'amministratore.",
          { duration: 5000 }
        );
        setAppleLoading(false);
        return;
      }

      // Use Apple's redirect flow (more reliable than popup)
      const redirectURI = `${window.location.origin}/api/auth/apple/callback`;
      const state = window.location.origin;
      const scope = "name email";

      const appleAuthUrl = new URL("https://appleid.apple.com/auth/authorize");
      appleAuthUrl.searchParams.set("client_id", client_id);
      appleAuthUrl.searchParams.set("redirect_uri", redirectURI);
      appleAuthUrl.searchParams.set("response_type", "code id_token");
      appleAuthUrl.searchParams.set("response_mode", "form_post");
      appleAuthUrl.searchParams.set("scope", scope);
      appleAuthUrl.searchParams.set("state", state);

      // Redirect to Apple
      window.location.href = appleAuthUrl.toString();
    } catch (err) {
      toast.error("Errore durante l'accesso con Apple. Riprova.");
      setAppleLoading(false);
    }
  };

  const [resetResult, setResetResult] = useState(null);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl border-[rgba(226,114,91,0.15)] bg-white max-w-md shadow-warm-lg" data-testid="auth-modal">
        <DialogHeader className="pb-4">
          <DialogTitle className="font-heading text-2xl font-bold text-[#2A1B14] text-center">
            {mode === "login" ? t("auth.login") : t("auth.register")}
          </DialogTitle>
          <p className="text-sm text-[#6A564A] text-center mt-1">
            {mode === "login" ? "Bentornato su BookEasy" : "Crea il tuo account gratuito"}
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <Button
              onClick={handleGoogleLogin}
              className="w-full rounded-full py-6 bg-white text-[#2A1B14] border-2 border-[rgba(226,114,91,0.15)] hover:bg-[#FDFBF7] hover:border-[#E2725B]/30 font-medium shadow-warm hover:shadow-warm-lg transition-all duration-200"
              data-testid="google-login-btn"
            >
              <GoogleIcon />
              <span className="ml-3">{t("auth.google")}</span>
            </Button>

            <Button
              onClick={handleAppleLogin}
              disabled={appleLoading}
              className="w-full rounded-full py-6 bg-[#2A1B14] text-white hover:bg-[#2A1B14]/90 font-medium shadow-lg hover:shadow-xl transition-all duration-200"
              data-testid="apple-login-btn"
            >
              {appleLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <AppleIcon />
              )}
              <span className="ml-3">{appleLoading ? "Collegamento Apple..." : t("auth.apple")}</span>
            </Button>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[rgba(226,114,91,0.1)]" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-[#6A564A]">{t("auth.or")}</span>
            </div>
          </div>

          <form onSubmit={handleEmailAuth} className="space-y-4">
            {/* Saved emails - quick select */}
            {mode === "login" && savedEmails.length > 0 && (
              <div className="space-y-1.5" data-testid="saved-emails-list">
                <p className="text-xs font-medium text-[#6A564A] px-1">Account salvati:</p>
                {savedEmails.map(email => (
                  <div
                    key={email}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-all ${
                      form.email === email
                        ? "border-[#E2725B] bg-[#E2725B]/5 shadow-sm"
                        : "border-[rgba(226,114,91,0.15)] hover:border-[#E2725B]/40 hover:bg-[#FDFBF7]"
                    }`}
                    onClick={() => setForm({ ...form, email })}
                    data-testid={`saved-email-${email}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${form.email === email ? "bg-[#E2725B]" : "bg-[#6A564A]"}`}>
                      {email[0].toUpperCase()}
                    </div>
                    <span className="text-sm text-[#2A1B14] font-medium flex-1 truncate">{email}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeSavedEmail(email); setSavedEmails(getSavedEmails()); }}
                      className="p-1 rounded-full hover:bg-red-50 text-[#6A564A] hover:text-red-500 transition-colors"
                      title="Rimuovi"
                      data-testid={`remove-email-${email}`}
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#E2725B]/50" strokeWidth={1.5} />
                <Input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("auth.name")}
                  className="rounded-full pl-12 py-6 border-[rgba(226,114,91,0.15)] focus:border-[#E2725B] focus:ring-[#E2725B]/20"
                  required
                  data-testid="auth-name-input"
                />
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#E2725B]/50" strokeWidth={1.5} />
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("auth.email")}
                className="rounded-full pl-12 py-6 border-[rgba(226,114,91,0.15)] focus:border-[#E2725B] focus:ring-[#E2725B]/20"
                required
                data-testid="auth-email-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#E2725B]/50" strokeWidth={1.5} />
              <Input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={t("auth.password")}
                className="rounded-full pl-12 pr-12 py-6 border-[rgba(226,114,91,0.15)] focus:border-[#E2725B] focus:ring-[#E2725B]/20"
                required
                minLength={6}
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#E2725B]/50 hover:text-[#E2725B] transition-colors"
                data-testid="toggle-password-btn"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full py-6 bg-gradient-to-r from-[#E2725B] to-[#F39C12] text-white font-bold shadow-xl shadow-[#E2725B]/20 hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 border-0"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                mode === "login" ? t("auth.login") : t("auth.register")
              )}
            </Button>

            {mode === "login" && (
              <button
                type="button"
                onClick={async () => {
                  if (!form.email) { toast.error("Inserisci la tua email prima"); return; }
                  try {
                    const res = await axios.post(`${API}/auth/reset-password`, { email: form.email });
                    setResetResult(res.data);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Errore");
                  }
                }}
                className="w-full text-center text-sm text-[#E2725B] hover:underline font-medium py-1"
                data-testid="forgot-password-btn"
              >
                Password dimenticata?
              </button>
            )}

            {/* Reset password result */}
            {resetResult && (
              <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4 space-y-2" data-testid="reset-result">
                <p className="text-sm font-bold text-emerald-700">Password resettata!</p>
                <p className="text-xs text-emerald-600">Email: <strong>{resetResult.email}</strong></p>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-emerald-200">
                  <span className="text-base font-mono font-bold text-[#09090B] flex-1">{resetResult.new_password}</span>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(resetResult.new_password); toast.success("Copiata!"); }}
                    className="text-xs bg-emerald-500 text-white px-3 py-1 rounded-lg font-bold hover:bg-emerald-600"
                    data-testid="copy-password-btn"
                  >
                    Copia
                  </button>
                </div>
                <p className="text-[10px] text-emerald-500">Usa questa password per accedere. Puoi cambiarla dopo il login.</p>
              </div>
            )}
          </form>

          <div className="text-center pt-4">
            <p className="text-sm text-[#6A564A]">
              {mode === "login" ? t("auth.no_account") : t("auth.have_account")}{" "}
              <button
                type="button"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="text-[#E2725B] font-bold hover:underline"
                data-testid="auth-toggle-mode"
              >
                {mode === "login" ? t("auth.register") : t("auth.login")}
              </button>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
